import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"
const persistConfig = {
    key: "auth",
    version: 2,
    storage,
    // Feature flags must come from login/renew Auth payload, not stale localStorage.
    blacklist: [
        'isLineItemMergeV2Enabled',
        'isTravelModuleAccessAvailable',
        'isReceiptVaultLineItemsVersion',
        'isReceiptVaultRulesDisplayOnUploadPopup',
        'isReceiptVaultRulesAcknowledgementRequired',
        'isSurveysModuleEnabled',
        'isExpenseAnomaliesModuleEnabled',
        'isExpenseAnomaliesEnabledForManager',
    ],
}

export interface UserPermission {
    permissionId: number | null,
    lst_PermissionId: number | null,
    key: string | null;
    value: boolean | null;
}
export interface AuthState {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    role: string | null;
    designationName: string | null;
    departmentName: string | null;
    roleID: number | null;
    contactPhone: number | null;
    businessPhone: number | null;
    businessExt: number | null;
    userAccountID: number | null;
    userInOrgId: number | null;
    token: string | null;
    isImpersonating: boolean | null;
    expiresAt: string | null;
    locationType: number | null;
    expenseType: number | null;
    location: number | null;
    locationName: string | null;
    baseURL: string | null;
    microServiceURL: string | null;
    reportPermision: boolean | null;
    isAllowCorporateCreditCardApprovalQueue: boolean | null;
    isAllowCorporateCreditCardOrderRequests: boolean | null;
    timeZone: string | null;
    isHomeAddressPopup: boolean | null;
    isCreditCardInfoPopup: boolean | null;
    userPermission: UserPermission[] | null
    webVersion: string | null;
    isCorpCreditCardRequiredPopUp: boolean | null;
     isOrgUserImpersonate: boolean | null;
    isTravelModuleAccessAvailable: boolean | null;
    isLineItemMergeV2Enabled: boolean | null;
    /** Org setting IsSurveysModuleEnabled */
    isSurveysModuleEnabled: boolean | null;
    /** Org setting IsExpenseAnomaliesModuleEnabled */
    isExpenseAnomaliesModuleEnabled: boolean | null;
    /** Org setting IsExpenseAnomaliesEnabledForManager */
    isExpenseAnomaliesEnabledForManager: boolean | null;
    /** Org setting IsReceiptVaultLineItemsVersion: 'v1' | 'v2' */
    isReceiptVaultLineItemsVersion: string | null;
    isReceiptVaultRulesDisplayOnUploadPopup: boolean | null;
    isReceiptVaultRulesAcknowledgementRequired: boolean | null;
    isReceiptVaultRulesAcknowledgementAccepted: boolean | null;
}

const initialState: AuthState = {
    firstName: null,
    lastName: null,
    email: null,
    role: null,
    designationName: null,
    departmentName: null,
    roleID: null,
    contactPhone: null,
    businessPhone: null,
    businessExt: null,
    userAccountID: null,
    userInOrgId: null,
    token: null,
    isImpersonating: false,
    expiresAt: null,
    locationType: null,
    expenseType: null,
    location: null,
    locationName: null,
    reportPermision: null,
    isAllowCorporateCreditCardApprovalQueue: null,
    isAllowCorporateCreditCardOrderRequests: null,
    baseURL: null,
    microServiceURL: null,
    timeZone: null,
    isHomeAddressPopup: null,
    isCreditCardInfoPopup: null,
    userPermission: null,
    webVersion: null,
    isCorpCreditCardRequiredPopUp: null,
     isOrgUserImpersonate: null,
    isTravelModuleAccessAvailable: false,
    isLineItemMergeV2Enabled: false,
    isSurveysModuleEnabled: true,
    isExpenseAnomaliesModuleEnabled: true,
    isExpenseAnomaliesEnabledForManager: false,
    isReceiptVaultLineItemsVersion: 'v2',
    isReceiptVaultRulesDisplayOnUploadPopup: false,
    isReceiptVaultRulesAcknowledgementRequired: false,
    isReceiptVaultRulesAcknowledgementAccepted: false,
}

export const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        setUser: (state, action: PayloadAction<{
            firstName: string,
            lastName: string,
            email: string,
            role: string,
            roleID: number,
            designationName?: string,
            departmentName?: string,
            contactPhone: number,
            businessPhone: number,
            businessExt: number,
            userAccountID: number,
            userInOrgId: number,
            token: string,
            expiresAt: string,
            locationType: number,
            expenseType: number,
            location: number,
            locationName: string,
            reportPermision: boolean,
            isAllowCorporateCreditCardApprovalQueue?: boolean,
            isAllowCorporateCreditCardOrderRequests?: boolean,
            baseURL: string,
            microServiceURL: string,
            timeZone: string,
            isHomeAddressPopup: boolean,
            isCreditCardInfoPopup: boolean,
            webVersion: string,
            userPermission: UserPermission[],
            isCorpCreditCardRequiredPopUp: boolean,
            isOrgUserImpersonate: boolean,
            isTravelModuleAccessAvailable: boolean,
            isLineItemMergeV2Enabled?: boolean,
            isSurveysModuleEnabled?: boolean,
            isExpenseAnomaliesModuleEnabled?: boolean,
            isExpenseAnomaliesEnabledForManager?: boolean,
            isReceiptVaultLineItemsVersion?: string,
            isReceiptVaultRulesDisplayOnUploadPopup?: boolean,
            isReceiptVaultRulesAcknowledgementRequired?: boolean,
            isReceiptVaultRulesAcknowledgementAccepted?: boolean,

        }>) => {
            localStorage.setItem("user", JSON.stringify({
                token: action.payload.token,
                baseURL: action.payload.baseURL,
                microServiceURL: action.payload.microServiceURL
            }));
            state.token = action.payload.token
            state.expiresAt = action.payload.expiresAt
            state.baseURL = action.payload.baseURL
            state.microServiceURL = action.payload.microServiceURL
            state.webVersion = action.payload.webVersion
            state.firstName = action.payload.firstName
            state.lastName = action.payload.lastName
            state.email = action.payload.email
            state.role = action.payload.role
            state.designationName = action.payload.designationName ?? null
            state.departmentName = action.payload.departmentName ?? null
            state.roleID = action.payload.roleID
            state.contactPhone = action.payload.contactPhone
            state.businessPhone = action.payload.businessPhone
            state.businessExt = action.payload.businessExt
            state.userAccountID = action.payload.userAccountID
            state.userInOrgId = action.payload.userInOrgId
            state.locationType = action.payload.locationType
            state.expenseType = action.payload.expenseType
            state.location = action.payload.location
            state.locationName = action.payload.locationName
            state.reportPermision = action.payload.reportPermision
            if (action.payload.isAllowCorporateCreditCardApprovalQueue !== undefined) {
                state.isAllowCorporateCreditCardApprovalQueue = action.payload.isAllowCorporateCreditCardApprovalQueue
            }
            if (action.payload.isAllowCorporateCreditCardOrderRequests !== undefined) {
                state.isAllowCorporateCreditCardOrderRequests = action.payload.isAllowCorporateCreditCardOrderRequests
            }
            state.timeZone = action.payload.timeZone
            state.isHomeAddressPopup = action.payload.isHomeAddressPopup
            state.isCreditCardInfoPopup = action.payload.isCreditCardInfoPopup
            state.userPermission = action.payload.userPermission
            state.isCorpCreditCardRequiredPopUp = action.payload.isCorpCreditCardRequiredPopUp
            state.isOrgUserImpersonate = action.payload.isOrgUserImpersonate
            state.isTravelModuleAccessAvailable = !!action.payload.isTravelModuleAccessAvailable
            // Strict boolean — only true enables Merge V2
            state.isLineItemMergeV2Enabled = action.payload.isLineItemMergeV2Enabled === true
            state.isSurveysModuleEnabled = action.payload.isSurveysModuleEnabled !== false
            state.isExpenseAnomaliesModuleEnabled = action.payload.isExpenseAnomaliesModuleEnabled !== false
            state.isExpenseAnomaliesEnabledForManager = action.payload.isExpenseAnomaliesEnabledForManager === true
            if (action.payload.isReceiptVaultLineItemsVersion !== undefined) {
                const v = String(action.payload.isReceiptVaultLineItemsVersion || 'v2').toLowerCase()
                state.isReceiptVaultLineItemsVersion = v === 'v1' || v === '1' ? 'v1' : 'v2'
            }
            if (action.payload.isReceiptVaultRulesDisplayOnUploadPopup !== undefined) {
                state.isReceiptVaultRulesDisplayOnUploadPopup =
                    !!action.payload.isReceiptVaultRulesDisplayOnUploadPopup
            }
            if (action.payload.isReceiptVaultRulesAcknowledgementRequired !== undefined) {
                state.isReceiptVaultRulesAcknowledgementRequired =
                    !!action.payload.isReceiptVaultRulesAcknowledgementRequired
            }
            if (action.payload.isReceiptVaultRulesAcknowledgementAccepted !== undefined) {
                state.isReceiptVaultRulesAcknowledgementAccepted = !!action.payload.isReceiptVaultRulesAcknowledgementAccepted
            }
            state.firstName = action.payload.firstName
            state.lastName = action.payload.lastName
            state.email = action.payload.email
            state.role = action.payload.role
            state.designationName = action.payload.designationName ?? null
            state.departmentName = action.payload.departmentName ?? null
            state.roleID = action.payload.roleID
            state.contactPhone = action.payload.contactPhone
            state.businessPhone = action.payload.businessPhone
            state.businessExt = action.payload.businessExt
            state.userAccountID = action.payload.userAccountID
            state.userInOrgId = action.payload.userInOrgId
            state.locationType = action.payload.locationType
            state.expenseType = action.payload.expenseType
            state.location = action.payload.location
            state.locationName = action.payload.locationName
            state.reportPermision = action.payload.reportPermision
            if (action.payload.isAllowCorporateCreditCardApprovalQueue !== undefined) {
                state.isAllowCorporateCreditCardApprovalQueue = action.payload.isAllowCorporateCreditCardApprovalQueue
            }
            if (action.payload.isAllowCorporateCreditCardOrderRequests !== undefined) {
                state.isAllowCorporateCreditCardOrderRequests = action.payload.isAllowCorporateCreditCardOrderRequests
            }
            state.timeZone = action.payload.timeZone
            state.isHomeAddressPopup = action.payload.isHomeAddressPopup
            state.isCreditCardInfoPopup = action.payload.isCreditCardInfoPopup
        },

        logout: (state) => {
            // localStorage.clear();
            localStorage.removeItem('user');
            localStorage.removeItem('originalEmail');
            localStorage.removeItem('persist:auth');
            state.firstName = null;
            state.lastName = null;
            state.email = null;
            state.role = null;
            state.designationName = null;
            state.departmentName = null;
            state.roleID = null;
            state.contactPhone = null;
            state.businessPhone = null;
            state.businessExt = null;
            state.userAccountID = null;
            state.userInOrgId = null;
            state.token = null;
            state.isImpersonating = false;
            state.expiresAt = null;
            state.locationType = null;
            state.expenseType = null;
            state.location = null;
            state.locationName = null;
            state.reportPermision = null;
            state.isAllowCorporateCreditCardApprovalQueue = null;
            state.isAllowCorporateCreditCardOrderRequests = null;
            state.baseURL = null;
            state.microServiceURL = null;
            state.timeZone = null;
            state.isHomeAddressPopup = null;
            state.isCreditCardInfoPopup = null;
            state.userPermission = null;
            state.webVersion = null;
            state.isCorpCreditCardRequiredPopUp = null;
            state.isOrgUserImpersonate = null;
            state.isTravelModuleAccessAvailable = false;
            state.isLineItemMergeV2Enabled = false;
            state.isSurveysModuleEnabled = true;
            state.isExpenseAnomaliesModuleEnabled = true;
            state.isExpenseAnomaliesEnabledForManager = false;
            state.isReceiptVaultLineItemsVersion = 'v2';
            state.isReceiptVaultRulesDisplayOnUploadPopup = false;
            state.isReceiptVaultRulesAcknowledgementRequired = false;
            state.isReceiptVaultRulesAcknowledgementAccepted = false;
        },

        startImpersonating: (state) => {
            state.isImpersonating = true;
        },
        stopImpersonating: (state) => {
            state.isImpersonating = false;
        },
        setOriginalEmail: (state, action: PayloadAction<string>) => {
            localStorage.setItem("originalEmail", action.payload);
        },
    }
})

export const selectAuth = (state: RootState) => state.auth
// export const selectBaseURL = (state: RootState) => state.auth.baseURL;
export const { setUser, logout, startImpersonating, stopImpersonating, setOriginalEmail,} = authSlice.actions;
export default persistReducer(persistConfig, authSlice.reducer);

// export default authSlice.reducer;